import React, { useState, useEffect, useCallback, useRef, Fragment, useMemo } from 'react';

import {
  Button,
  FormControl,
  ButtonToolbar,
  InputGroup,
  Row,
  Col,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { Lens, IcoPlus, TooltipInfoIcon, DownloadFile } from '@icons';
import {
  getTranslations,
  getStats,
  getProjects,
  getInterventions,
  getFinancings,
  getStructureClusters,
  exportExcel,
  getEsgTimeSeries,
  getSroiTimeSeries,
  getProjectsByRegion,
  getSroiAvgByRegion,
  getInvestmentByProponentType,
  getSroiByProponentType,
  getProjectsDistribution,
  getChartStats,
  getEsgAvgByRegion,
  getDistribProponentType,
  getDistribMacroArea,
  getDistribInvestmentClass,
  getSroiByMacroSector,
  getSroiAvgByIntervention,
  getEsgAvgByIntervention,
  getTableSroiByInvestmentClass,
  getTableEsgByMacroSector,
  getTableEsgByProponentGroup,
  getTableSroiByMacroArea,
} from '@lib/api';
import { AgGridReact } from 'ag-grid-react/lib/agGridReact';
import '@ag-grid-community/core/dist/styles/ag-grid.css';
import '@ag-grid-community/core/dist/styles/ag-theme-alpine.css';
import AG_GRID_LOCALE_IT from '@components/projects/ag-grid-translate/locale-it';
import ActionBtnRenderer from '@components/projects/actionBtnRenderer/ActionBtnRenderer';
import RadarChartGradient from '@components/projects/radarChart/RadarChart';
import EsgTimingBarChart from 'components/charts/esgTimingBarChart';
import SroiTimingBarChart from 'components/charts/sroiTimingBarChart';
import {
  calculateAverageScore,
  filterAndSortByValue,
  formatValue,
  mapProponentData,
  mapScoreToRating,
  mergeAvgAndStdDev,
} from '@lib/helpers/homepageHelpers';
import { proponentsOptionsLabel } from '@lib/const/selectOptionsConstants';
import HomeDonutChart from 'components/charts/homeDonutChart';
import { MetricsTable } from 'components/metricsTable/metricsTable.js';
import { DangerToast } from 'components/GlobalToastList';
import RegionalHeatmapChart from 'components/charts/regionalHeatmapChart';
import DistributionAverageVerticalHistogram from 'components/charts/distributionAverageVerticalHistogram';
import SingleBarVerticalHistogram from 'components/charts/singleBarVerticalHistogram';
import HorizontalBarChart from 'components/charts/horizontalBarChart';

const esgLabel = ['E', 'S', 'G', 'ESG'];

const ProjectsTab = () => {
  // Superadmin vede tutti i grafici G-01..G-13. Admin (role 3) vede solo le serie
  // storiche T-01/T-02 + la dashboard corrente. Flag dal login (data.isSuperadmin).
  const isSuperAdmin = localStorage.getItem('ics_superadmin') === 'true';
  const [translations, setTranslations] = useState([]);
  const [projects, setProjects] = useState(null);
  const [interventions, setInterventions] = useState([]);
  const [financing, setFinancing] = useState([]);
  const [cluster, setCluster] = useState([]);
  const [optionsPagination, setOptionsPagination] = useState([]);
  const [stats, setStats] = useState({});
  const [dataRating, setDataRating] = useState(0);
  const [avgRating, setAvgRating] = useState('');
  const [scoreEsg, setScoreEsg] = useState('');
  const [dataBenefits, setDataBenefits] = useState([]);
  const [dataSroi, setDataSroi] = useState([]);
  const [avgBenefits, setAvgBenefits] = useState([]);
  const [avgData, setAvgData] = useState([]);
  const [noOutData, setNoOutData] = useState([]);
  const [esgTimeSeriesRating, setEsgTimeSeriesRating] = useState([]);
  const [sroiTimeSeriesRating, setSroiTimeSeriesRating] = useState([]);
  const [projectsByRegion, setProjectsByRegion] = useState([]);
  const [sroiAvgByRegion, setSroiAvgByRegion] = useState([]);
  const [investmentByProponentType, setInvestmentByProponentType] = useState([]);
  const [sroiByProponentType, setSroiByProponentType] = useState([]);
  const [projectsDistribution, setProjectsDistribution] = useState([]);
  const [esgAvgByRegion, setEsgAvgByRegion] = useState([]);
  const [distribProponentType, setDistribProponentType] = useState([]);
  const [distribMacroArea, setDistribMacroArea] = useState([]);
  const [distribInvestmentClass, setDistribInvestmentClass] = useState([]);
  const [sroiByMacroSector, setSroiByMacroSector] = useState([]);
  const [sroiAvgByIntervention, setSroiAvgByIntervention] = useState([]);
  const [esgAvgByIntervention, setEsgAvgByIntervention] = useState([]);
  const [tableSroiByInvestmentClass, setTableSroiByInvestmentClass] = useState([]);
  const [tableEsgByMacroSector, setTableEsgByMacroSector] = useState([]);
  const [tableEsgByProponentGroup, setTableEsgByProponentGroup] = useState([]);
  const [tableSroiByMacroArea, setTableSroiByMacroArea] = useState([]);
  // Stato condiviso per l'apertura dei due accordion "Dettaglio ESG" / "Dettaglio SROI":
  // un singolo click su uno dei due summary apre/chiude entrambi (richiesta business).
  const [dettagliOpen, setDettagliOpen] = useState(false);
  const toggleDettagli = useCallback((e) => {
    e.preventDefault();
    setDettagliOpen((open) => !open);
  }, []);
  const gridRef = useRef();
  const searchTermRef = useRef('');
  const searchDebounceRef = useRef(null);
  const [refresh, setRefresh] = useState(false);
  const localeText = AG_GRID_LOCALE_IT;
  const [isLoading, setIsLoading] = useState(false);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [chartsData, setChartsData] = useState({});

  useEffect(() => {
    let isCancelled = false;

    const loadDashboardData = async () => {
      setIsDashboardLoading(true);

      try {
        const [
          retrievedTranslation,
          retrievedStats,
          retrievedInterventions,
          retrievedFinancings,
          retrievedCluster,
          retrievedChartsStats,
        ] = await Promise.all([
          getTranslations(),
          getStats(),
          getInterventions(),
          getFinancings(),
          getStructureClusters(),
          getChartStats(),
        ]);

        if (isCancelled) return;

        setTranslations(retrievedTranslation);

        // 👇 salva charts data
        setChartsData(retrievedChartsStats);

        if (retrievedStats && Object.keys(retrievedStats).length > 0) {
          const {
            avgRating,
            avgScoreNoOut,
            stdDevNoOut,
            stdDevSroiProponentNoOut,
            avgProponentSroiNoOut,
            totalProponentBenefits,
            stdDevSroiProponent,
            avgProponentSroi,
            avgPortfolioValue,
            esgOutlook,
          } = retrievedStats;

          setStats(retrievedStats);

          setAvgData([
            { name: 'Rating Medio', ...avgRating },
            { name: 'Score Medio', ...avgScoreNoOut },
            { name: 'Deviazione Standard', ...stdDevNoOut },
          ]);

          setNoOutData(mergeAvgAndStdDev(avgProponentSroiNoOut, stdDevSroiProponentNoOut));

          setDataBenefits(
            mapProponentData(filterAndSortByValue(totalProponentBenefits), proponentsOptionsLabel),
          );

          setAvgBenefits(
            mapProponentData(filterAndSortByValue(stdDevSroiProponent), proponentsOptionsLabel).map(
              (item) => ({ ...item, type: 'avg' }),
            ),
          );

          setDataSroi(
            mapProponentData(filterAndSortByValue(avgProponentSroi), proponentsOptionsLabel),
          );

          setScoreEsg(formatValue(avgPortfolioValue));
          setDataRating(esgOutlook?.percentage || 0);
        }

        setInterventions(retrievedInterventions);
        setFinancing(retrievedFinancings);
        setCluster(retrievedCluster);
      } catch (error) {
        if (!isCancelled) {
          console.error('Errore durante il caricamento delle statistiche/dashboard:', error);
          DangerToast(
            'Attenzione',
            'Si è verificato un errore durante il caricamento dei dati della dashboard',
          );
        }
      } finally {
        if (!isCancelled) {
          setIsDashboardLoading(false);
        }
      }
    };

    loadDashboardData();

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    getEsgTimeSeries().then((retrievedEsgTimeSeries) => {
      setEsgTimeSeriesRating(retrievedEsgTimeSeries);
    });

    getSroiTimeSeries().then((retrievedSroiTimeSeries) => {
      setSroiTimeSeriesRating(retrievedSroiTimeSeries);
    });

    getProjectsByRegion().then((retrieved) => {
      setProjectsByRegion(Array.isArray(retrieved) ? retrieved : []);
    });

    getSroiAvgByRegion().then((retrieved) => {
      setSroiAvgByRegion(Array.isArray(retrieved) ? retrieved : []);
    });

    getInvestmentByProponentType().then((retrieved) => {
      setInvestmentByProponentType(Array.isArray(retrieved) ? retrieved : []);
    });

    getSroiByProponentType().then((retrieved) => {
      setSroiByProponentType(Array.isArray(retrieved) ? retrieved : []);
    });

    getProjectsDistribution().then((retrieved) => {
      setProjectsDistribution(Array.isArray(retrieved) ? retrieved : []);
    });

    // Nuovi endpoint BE-1/BE-2/BE-3 per i grafici G-01..G-13 della pagina Progetti.
    getEsgAvgByRegion().then((r) => setEsgAvgByRegion(Array.isArray(r) ? r : []));
    getDistribProponentType().then((r) => setDistribProponentType(Array.isArray(r) ? r : []));
    getDistribMacroArea().then((r) => setDistribMacroArea(Array.isArray(r) ? r : []));
    getDistribInvestmentClass().then((r) => setDistribInvestmentClass(Array.isArray(r) ? r : []));
    getSroiByMacroSector().then((r) => setSroiByMacroSector(Array.isArray(r) ? r : []));
    getSroiAvgByIntervention().then((r) => setSroiAvgByIntervention(Array.isArray(r) ? r : []));
    getEsgAvgByIntervention().then((r) => setEsgAvgByIntervention(Array.isArray(r) ? r : []));
    getTableSroiByInvestmentClass().then((r) => setTableSroiByInvestmentClass(Array.isArray(r) ? r : []));
    getTableEsgByMacroSector().then((r) => setTableEsgByMacroSector(Array.isArray(r) ? r : []));
    getTableEsgByProponentGroup().then((r) => setTableEsgByProponentGroup(Array.isArray(r) ? r : []));
    getTableSroiByMacroArea().then((r) => setTableSroiByMacroArea(Array.isArray(r) ? r : []));
  }, []);

  useEffect(() => {
    setOptionsPagination([
      <Fragment key='options'>
        <option defaultValue='20' key='20'>
          20
        </option>
        <option value='50' key='50'>
          50
        </option>
        <option value='100' key='100'>
          100
        </option>
      </Fragment>,
    ]);
  }, []);

  useEffect(() => {
    if (stats?.avgPortfolio) {
      setAvgRating(stats.avgPortfolio);
      return;
    }

    if (projects?.length > 0) {
      const avgScore = calculateAverageScore(projects);
      setAvgRating(mapScoreToRating(avgScore));
    }
  }, [projects, stats]);

  useEffect(() => {
    if (refresh) {
      // In modalità infinite il refresh si traduce in un purge della cache;
      // AG Grid richiamerà il datasource per le pagine visualizzate.
      if (gridRef.current?.api) {
        gridRef.current.api.purgeInfiniteCache();
      }
      setRefresh(false);
    }
  }, [refresh]);

  useEffect(() => {
    functionDrawRadarGradient();
  }, [dataBenefits]);

  useEffect(() => {
    if (isLoading) {
      exportExcel().then(() => {});
      setIsLoading(false);
    }
  }, [isLoading]);

  const functionDrawRadarGradient = (data, secondData, color, secondColor, divId, maxValue) => {
    const axisIndexMap = {};
    data?.forEach((item, index) => {
      axisIndexMap[item.axis] = index;
    });

    secondData?.sort((a, b) => {
      const indexA = axisIndexMap[a.axis];
      const indexB = axisIndexMap[b.axis];
      return indexA - indexB;
    });

    const mergedData = data && secondData && [...data, ...secondData];
    const highestValue = mergedData && Math.max(...mergedData?.map((el) => el.value));

    return (
      <RadarChartGradient
        data={[data, secondData]}
        radarColor={color}
        secondRadarColor={secondColor}
        divId={divId}
        maxValue={highestValue || maxValue}
      />
    );
  };

  // Search server-side con debounce: aggiorna il ref usato dal datasource e
  // purga la cache di AG Grid per riemettere getRows con la nuova search.
  const onFilterTextBoxChanged = useCallback((e) => {
    searchTermRef.current = e.target.value || '';
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      if (gridRef.current?.api) {
        gridRef.current.api.purgeInfiniteCache();
      }
    }, 300);
  }, []);

  // Datasource AG Grid (infinite row model). Converte startRow/endRow in
  // page/per_page e applica sort/search server-side.
  const SORT_COLUMN_MAP = useMemo(() => ({
    clientId: 'client_id',
    requestId: 'request_id',
  }), []);

  const infiniteDataSource = useMemo(() => ({
    getRows: async (params) => {
      const perPage = Math.max(1, params.endRow - params.startRow);
      const page = Math.floor(params.startRow / perPage) + 1;
      const queryParams = { page, per_page: perPage };
      if (searchTermRef.current) queryParams.search = searchTermRef.current;
      if (params.sortModel && params.sortModel.length > 0) {
        const m = params.sortModel[0];
        const mapped = SORT_COLUMN_MAP[m.colId];
        if (mapped) {
          queryParams.sort_by = mapped;
          queryParams.sort_order = m.sort;
        }
      }
      try {
        const res = await getProjects(queryParams);
        const data = Array.isArray(res?.data) ? res.data : [];
        const total = typeof res?.total === 'number' ? res.total : data.length;
        params.successCallback(data, total);
      } catch (err) {
        console.error('getProjects failed', err);
        params.failCallback();
      }
    },
  }), [SORT_COLUMN_MAP]);

  const onGridReady = useCallback(() => {
    // Il datasource è passato via prop; nessuna fetch eager da fare qui.
  }, []);

  const autoSizeAll = useCallback((skipHeader) => {
    const allColumnIds = [];
    gridRef.current.columnApi.getAllColumns().forEach((column) => {
      allColumnIds.push(column.getId());
    });
    gridRef.current.columnApi.autoSizeColumns(allColumnIds, skipHeader);
  }, []);

  const onFirstDataRendered = useCallback(() => {
    if (window.innerWidth >= 2540) {
      gridRef.current.api.sizeColumnsToFit();
    } else {
      autoSizeAll(false);
    }
  }, [autoSizeAll]);

  const onModelUpdated = useCallback(() => {
    if (window.innerWidth >= 2540) {
      gridRef.current.api.sizeColumnsToFit();
    } else {
      autoSizeAll(false);
    }
  }, [autoSizeAll]);

  const onPageSizeChanged = useCallback(() => {
    let value = document.getElementById('page-size').value;
    gridRef.current.api.paginationSetPageSize(Number(value));
  }, []);

  const jsxInCell = (params) => {
    if (!params.data) return null;
    return (
      <div className='d-flex justify-content-between align-items-center'>
        {params.data.requestId} <TooltipInfoIcon style={{ width: '1rem', minWidth: '1rem' }} />
      </div>
    );
  };

  // autoHeight non è supportato in 'infinite' row model: AG Grid lo segnala
  // a console e applica altezza standard. Rimosso per evitare il warning.
  const defaultColDef = {
    resizable: true,
    wrapText: true,
    flex: 1,
    suppressMovable: true,
    cellStyle: { padding: '0 0.7rem' },
  };

  const columnDefs = [
    {
      headerName: translations
        .filter((trans) => trans.code === 'ICS002')
        .map((trans) => trans.text),
      field: 'clientId',
      filter: 'agTextColumnFilter',
    },
    {
      headerName: translations
        .filter((trans) => trans.code === 'ICS003')
        .map((trans) => trans.text),
      field: 'requestId',
      filter: 'agTextColumnFilter',
      tooltipField: 'description',
      cellRendererSelector: () => ({
        component: jsxInCell,
      }),
    },
    {
      headerName: translations
        .filter((trans) => trans.code === 'ICS005')
        .map((trans) => trans.text),
      field: 'proponents',
      filter: 'agTextColumnFilter',
      valueGetter: (params) => params.data?.proponents?.name ?? null,
    },
    {
      headerName: translations
        .filter((trans) => trans.code === 'ICS033')
        .map((trans) => trans.text),
      field: 'interventionClassCode',
      filter: 'agTextColumnFilter',
      valueGetter: (params) => {
        if (!params.data) return null;
        // Mostra il tipo di finanziamento reale (Ristrutturazione, Acquisto
        // nuove attrezzature, ...); fallback alla classe di intervento SROI
        // (Nuova costruzione, ...) quando il finanziamento non è valorizzato.
        // Coerente con la colonna "Tipo di intervento" del DB Excel.
        const interventionName = params.data.financingTypeCode
          ? financing
              .filter((financingItem) => financingItem.code === params.data.financingTypeCode)
              .map((filteredFinancing) => (filteredFinancing.text || '').toUpperCase())
          : interventions
              .filter((intervention) => intervention.code === params.data.interventionClassCode)
              .map((filteredIntervention) => (filteredIntervention.text || '').toUpperCase());

        return interventionName;
      },
    },
    {
      headerName: translations
        .filter((trans) => trans.code === 'ICS008')
        .map((trans) => trans.text),
      field: 'sector',
      filter: 'agTextColumnFilter',
      valueGetter: (params) => {
        if (!params.data) return null;
        const sectorName = params.data.structureClusterCode
          ? cluster
              .filter((clusterItem) => clusterItem.code === params.data.structureClusterCode)
              .map((filteredCluster) => `${params.data.sectors?.text ?? ''} - ${filteredCluster.cluster}`)
          : params.data.sectors?.text ?? null;
        return sectorName;
      },
    },
    {
      headerName: translations
        .filter((trans) => trans.code === 'ICS009')
        .map((trans) => trans.text),
      field: 'regions',
      filter: 'agTextColumnFilter',
      valueGetter: (params) => params.data?.locations?.nomeRegione ?? null,
    },
    {
      headerName: translations
        .filter((trans) => trans.code === 'ICS095')
        .map((trans) => trans.text),
      field: 'sroiratings',
      cellStyle: { textAlign: 'right' },
      valueGetter: (params) => {
        if (!params.data) return null;
        if (params.data.interventionClassCode !== 'NOINTPURCH') {
          const totalCapex = (params.data.sroiratings || [])
            .filter((esgrating) => esgrating.code === 'TOTALCAPEX')
            .map((filteredRating) =>
              filteredRating.value.toLocaleString('it-IT', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            );
          return totalCapex.length > 0 ? `${totalCapex} €` : '';
        } else {
          return params.data?.totalInvestment
            ? `${params.data.totalInvestment.toLocaleString('it-IT', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} €`
            : '';
        }
      },
    },
    {
      headerName: translations
        .filter((trans) => trans.code === 'ICS053')
        .map((trans) => trans.text),
      field: 'esgratings',
      cellStyle: { textAlign: 'center' },
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'equals'],
        defaultOption: 'equals',
      },
      valueGetter: (params) => {
        if (!params.data) return null;
        const rating = (params.data.esgratings || [])
          .filter((esgrating) => esgrating.type === 'total')
          .map((filteredRating) => filteredRating.rating);

        return rating;
      },
    },
    {
      headerName: translations
        .filter((trans) => trans.code === 'ICS096')
        .map((trans) => trans.text),
      field: 'sroiratings',
      filter: 'agTextColumnFilter',
      cellStyle: { textAlign: 'right' },
      valueGetter: (params) => {
        if (!params.data) return null;
        if (params.data.interventionClassCode !== 'NOINTPURCH') {
          return (params.data.sroiratings || [])
            .filter((sroi) => sroi.code === 'SROI')
            ?.map((filteredValue) =>
              filteredValue.value.toLocaleString('it-IT', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            );
        } else {
          return 'N.A.';
        }
      },
    },
    {
      headerName: translations
        .filter((trans) => trans.code === 'ICS052')
        .map((trans) => trans.text),
      field: 'status',
      cellStyle: (params) =>
        params.data?.status && params.data.status !== 'DRAFT'
          ? { color: '#08a2aa', fontWeight: 'bold', padding: '0 0.7rem' }
          : { color: 'black', padding: '0 0.7rem' },
      pinned: 'right',
      filter: 'agTextColumnFilter',
      valueGetter: (params) => {
        if (!params.data) return null;
        const status = params.data.status;
        return status === 'DRAFT' ? 'Bozza' : 'Inviato';
      },
    },
    {
      headerName: '',
      pinned: 'right',
      minWidth: 170,
      maxWidth: 200,
      cellStyle: { textAlign: 'center' },
      cellRenderer: ActionBtnRenderer,
      cellRendererParams: {
        setRefresh: setRefresh,
      },
    },
  ];

  const handleDownload = () => {
    setIsLoading(true);
  };

  const sroiDetailColumns = [{ key: 'value', label: '' }];

  const sroiDetailRows = [
    {
      id: 'avgVanBenefits',
      labelNode: (
        <>
          {translations
            .filter((trans) => trans.code === 'AVGVANBENEFITS')
            .map((trans) => trans.text)}
          {translations
            .filter((trans) => trans.code === 'AVGVANBENEFITS')
            .map((trans) => trans.description)
            .join() && (
            <OverlayTrigger
              placement='right'
              delay={{ show: 200, hide: 400 }}
              overlay={
                <Tooltip id='avgvanbenefits-tooltip'>
                  {translations
                    .filter((trans) => trans.code === 'VANBENEFITS')
                    .map((trans) => trans.description)}
                </Tooltip>
              }>
              <TooltipInfoIcon className='tooltip-ico mb-1' />
            </OverlayTrigger>
          )}
        </>
      ),
      value:
        stats && stats.totalBenefits
          ? `${stats.totalBenefits.toLocaleString('it-IT', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })} €`
          : 'N/A',
    },
    {
      id: 'avgVanCosts',
      labelNode: (
        <>
          {translations.filter((trans) => trans.code === 'AVGVANCOSTS').map((trans) => trans.text)}
          {translations
            .filter((trans) => trans.code === 'AVGVANCOSTS')
            .map((trans) => trans.description)
            .join() && (
            <OverlayTrigger
              placement='right'
              delay={{ show: 200, hide: 400 }}
              overlay={
                <Tooltip id='avgvancosts-tooltip'>
                  {translations
                    .filter((trans) => trans.code === 'VANCOSTS')
                    .map((trans) => trans.description)}
                </Tooltip>
              }>
              <TooltipInfoIcon className='tooltip-ico mb-1' />
            </OverlayTrigger>
          )}
        </>
      ),
      value:
        stats && stats.totalCosts
          ? `${stats.totalCosts.toLocaleString('it-IT', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })} €`
          : 'N/A',
    },
    {
      id: 'avgVane',
      labelNode: (
        <>
          {translations.filter((trans) => trans.code === 'AVGVANE').map((trans) => trans.text)}
          {translations
            .filter((trans) => trans.code === 'AVGVANE')
            .map((trans) => trans.description)
            .join() && (
            <OverlayTrigger
              placement='right'
              delay={{ show: 200, hide: 400 }}
              overlay={
                <Tooltip id='avgvane-tooltip'>
                  {translations
                    .filter((trans) => trans.code === 'VANE')
                    .map((trans) => trans.description)}
                </Tooltip>
              }>
              <TooltipInfoIcon className='tooltip-ico mb-1' />
            </OverlayTrigger>
          )}
        </>
      ),
      value:
        stats && stats.totalVANE
          ? `${stats.totalVANE.toLocaleString('it-IT', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })} €`
          : 'N/A',
    },
    {
      id: 'avgSroi',
      labelNode: (
        <>
          {translations.filter((trans) => trans.code === 'AVGSROI').map((trans) => trans.text)}

          <OverlayTrigger
            placement='right'
            delay={{ show: 200, hide: 400 }}
            overlay={
              <div
                className='d-flex p-3 f-500'
                style={{
                  backgroundColor: 'rgba(0,0,0,0.9)',
                  color: '#ffffff',
                  fontSize: '.9rem',
                  borderRadius: '5px',
                }}>
                <span className='mr-2'>
                  {translations
                    .filter((trans) => trans.code === 'ICS132')
                    .map((trans) => trans.text)}
                </span>
                <span>
                  ({' '}
                  {translations
                    .filter((trans) => trans.code === 'ICS133')
                    .map((trans) => trans.text)}{' '}
                  )
                </span>
              </div>
            }>
            <TooltipInfoIcon className='tooltip-ico mb-1' />
          </OverlayTrigger>

          {translations
            .filter((trans) => trans.code === 'AVGSROI')
            .map((trans) => trans.description)
            .join() && (
            <OverlayTrigger
              placement='right'
              delay={{ show: 200, hide: 400 }}
              overlay={
                <Tooltip id='avgsroi-tooltip'>
                  {translations
                    .filter((trans) => trans.code === 'AVGSROI')
                    .map((trans) => trans.description)}
                </Tooltip>
              }>
              <TooltipInfoIcon className='tooltip-ico mb-1' />
            </OverlayTrigger>
          )}
        </>
      ),
      value: (
        <>
          {stats && stats.avgSroi
            ? stats.avgSroi.toLocaleString('it-IT', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })
            : 'N/A'}
          <span className='ml-2 f-300'>
            {noOutData &&
              noOutData
                .filter((el) => el.proponent === 'portfolio')
                .map(
                  (el) =>
                    `(${el.avgValue.toLocaleString('it-IT', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })})`,
                )}
          </span>
        </>
      ),
    },
    {
      id: 'ics131',
      labelNode: (
        <span>
          {translations.filter((trans) => trans.code === 'ICS131').map((trans) => trans.text)}

          <OverlayTrigger
            placement='right'
            delay={{ show: 200, hide: 400 }}
            overlay={
              <div
                className='d-flex p-3 f-500'
                style={{
                  backgroundColor: 'rgba(0,0,0,0.9)',
                  color: '#ffffff',
                  fontSize: '.9rem',
                  borderRadius: '5px',
                }}>
                <span className='mr-2'>
                  {translations
                    .filter((trans) => trans.code === 'ICS132')
                    .map((trans) => trans.text)}
                </span>
                <span>
                  ({' '}
                  {translations
                    .filter((trans) => trans.code === 'ICS133')
                    .map((trans) => trans.text)}{' '}
                  )
                </span>
              </div>
            }>
            <TooltipInfoIcon className='tooltip-ico mb-1' />
          </OverlayTrigger>
        </span>
      ),
      value: (
        <>
          {stats &&
            stats?.stdDevSroiProponent
              ?.filter((el) => el.proponent === 'portfolio')
              .map((portfolioStat) =>
                portfolioStat.value.toLocaleString('it-IT', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                }),
              )}
          <span className='ml-2 f-300'>
            {noOutData &&
              noOutData
                ?.filter((el) => el.proponent === 'portfolio')
                .map(
                  (el) =>
                    `(${el.stdDevValue.toLocaleString('it-IT', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })})`,
                )}
          </span>
        </>
      ),
    },
  ];

  const downloadTooltip = (props) => <Tooltip {...props}>Download Excel</Tooltip>;

  return (
    <>
      <div className='dashboard dashboard-stack'>
        <Row className='report-first gap-3 p-0 d-flex'>
          <Col className='simple-box report-1 mt-0'>
            <Row>
              <h5 className='title-simple-box text-uppercase mt-1 pl-3'>Riepilogo</h5>
            </Row>
            <Row className='mt-3'>
              {Object.keys(stats).length > 0 && !isDashboardLoading ? (
                <>
                  <Col className='text-center my-3'>
                    <p className='text-capitalize'>numerosità</p>
                    <p className='my-4 font-lg-2'>{stats.projectsSubmitted}</p>
                    <p className='text-uppercase f-500 min-label-width'>progetti</p>
                  </Col>
                  <Col className='text-center my-3'>
                    <p className='text-capitalize'>euro</p>
                    <p className='my-4 font-lg-2'>
                      {stats.totalInvestments?.toLocaleString('it-IT', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </p>
                    <p className='text-uppercase f-500 min-label-width position-relative'>
                      {translations
                        .filter((trans) => trans.code === 'ICS043')
                        .map((trans) => trans.text)}
                      <OverlayTrigger
                        placement='bottom'
                        delay={{ show: 200, hide: 400 }}
                        overlay={
                          <Tooltip id='button-tooltip'>
                            {translations
                              .filter((trans) => trans.code === 'ICS043')
                              .map((trans) => trans.description)}
                          </Tooltip>
                        }>
                        <TooltipInfoIcon
                          className='tooltip-ico'
                          style={{
                            width: '1rem',
                            position: 'absolute',
                            top: '50%',
                            transform: 'translateY(-50%)',
                          }}
                        />
                      </OverlayTrigger>
                    </p>
                  </Col>
                  <Col className='text-center my-3'>
                    <p className='text-capitalize'>euro</p>
                    <p className='my-4 font-lg-2'>
                      {stats.avgInvestment?.toLocaleString('it-IT', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </p>
                    <p className='text-uppercase f-500 min-label-width position-relative'>
                      {translations
                        .filter((trans) => trans.code === 'ICS044')
                        .map((trans) => trans.text)}
                      <OverlayTrigger
                        placement='bottom'
                        delay={{ show: 200, hide: 400 }}
                        overlay={
                          <Tooltip id='button-tooltip'>
                            {translations
                              .filter((trans) => trans.code === 'ICS044')
                              .map((trans) => trans.description)}
                          </Tooltip>
                        }>
                        <TooltipInfoIcon
                          className='tooltip-ico'
                          style={{
                            width: '1rem',
                            position: 'absolute',
                            top: '50%',
                            transform: 'translateY(-50%)',
                          }}
                        />
                      </OverlayTrigger>
                    </p>
                  </Col>
                  <Col className='text-center my-3'>
                    <p className='text-capitalize'>anni</p>
                    <p className='my-4 font-lg-2'>
                      {stats.avgDuration?.toLocaleString('it-IT', { maximumFractionDigits: 1 })}
                    </p>
                    <p className='text-uppercase f-500 min-label-width'>durata media</p>
                  </Col>
                </>
              ) : (
                <div className='d-flex justify-content-center align-items-center w-100'>
                  <Spinner animation='border' role='status' className='my-3'>
                    <span style={{ visibility: 'hidden' }}>Loading...</span>
                  </Spinner>
                </div>
              )}
            </Row>
          </Col>
        </Row>

        {/* Card consolidate ESG/SROI (requisiti §3.2): 2 card affiancate 50/50,
            ognuna con sezione superiore "Portafoglio" + accordion "Dettaglio"
            aperto di default (HTML5 details/summary). */}
        <div className='d-flex justify-content-between px-0 col-12 flex-row gap-1 flex-wrap dashboard-block esg-sroi-cards'>
          {/* ============ Card ESG ============ */}
          <div className='p-0 d-flex' style={{ minWidth: '400px', flex: '1 1 0' }}>
            <Col className='simple-box d-flex flex-column col-12'>
              <Row className='ml-0'>
                <h5 className='title-simple-box text-uppercase mt-1 pl-0'>ESG</h5>
              </Row>

              {/* Portafoglio ESG (ICS126): donut + legenda + media */}
              <h6 className='subsection-title text-uppercase mt-3 pl-0' style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                {translations.filter((trans) => trans.code === 'ICS126').map((trans) => trans.text)}
              </h6>
              {dataRating && !isDashboardLoading ? (
                <Row className='mt-2 text-center align-items-center'>
                  <Col
                    id='donut-ratingEsg'
                    className='d-flex flex-column justify-content-center align-items-center'>
                    <HomeDonutChart chartId='ratingEsg' data={dataRating} scoreEsg={scoreEsg} />
                    <p className='text-uppercase f-500 min-label-width mt-4 position-relative'>
                      {translations
                        .filter((trans) => trans.code === 'ICS045')
                        .map((trans) => trans.text)}
                      <OverlayTrigger
                        placement='bottom'
                        delay={{ show: 200, hide: 400 }}
                        overlay={
                          <Tooltip id='button-tooltip'>
                            {translations
                              .filter((trans) => trans.code === 'ICS045')
                              .map((trans) => trans.description)}
                          </Tooltip>
                        }>
                        <TooltipInfoIcon
                          className='tooltip-ico'
                          style={{
                            width: '1rem',
                            position: 'absolute',
                            top: '50%',
                            transform: 'translateY(-50%)',
                          }}
                        />
                      </OverlayTrigger>
                    </p>
                  </Col>

                  <Col id='donut-legend' className='align-self-start'>
                    <Row className='flex-column align-content-center'>
                      <Row className='align-items-center'>
                        <span>Rating:</span>
                      </Row>

                      <Row className='min-label-width align-items-center'>
                        <div className='box green'></div>
                        <div className='legend-text'>
                          A+ <span>|</span> AA <span>|</span> AA+ <span>|</span> AAA
                          <br />
                          (score 60-100)
                        </div>
                      </Row>

                      <Row className='min-label-width align-items-center'>
                        <div className='box yellow'></div>
                        <div className='legend-text'>
                          BB <span>|</span> BBB <span>|</span> A
                          <br />
                          (score 30-60)
                        </div>
                      </Row>

                      <Row className='min-label-width align-items-center'>
                        <div className='box red'></div>
                        <div className='legend-text'>
                          C <span>|</span> B
                          <br />
                          (score 0-30)
                        </div>
                      </Row>
                    </Row>
                  </Col>

                  <Col id='media-portafoglio' className='text-center justify-content-center'>
                    <div style={{ width: 'fit-content', margin: '0 auto' }}>
                      <p className='mb-4 font-lg-2'>{avgRating === '' ? 'N/A' : avgRating}</p>
                      <p className='media-text text-uppercase f-500'>media portafoglio</p>
                    </div>
                  </Col>
                </Row>
              ) : (
                <div className='d-flex justify-content-center align-items-center w-100'>
                  <Spinner animation='border' role='status' className='my-3'>
                    <span style={{ visibility: 'hidden' }}>Loading...</span>
                  </Spinner>
                </div>
              )}

              {/* Accordion Dettaglio ESG (ICS128): chiuso di default, sincronizzato col Dettaglio SROI. */}
              <details className='accordion-detail mt-3' open={dettagliOpen}>
                <summary
                  className='subsection-title text-uppercase pl-0'
                  onClick={toggleDettagli}
                  style={{ fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', listStyle: 'none' }}>
                  <span style={{ display: 'inline-block', width: '1rem' }}>{dettagliOpen ? '▾' : '▸'}</span>
                  {translations.find((trans) => trans.code === 'ICS128')?.text}
                </summary>
                <div className='mt-2'>
                  <Col
                    style={{ marginLeft: '-15px' }}
                    className='sroi-details-padding d-flex justify-content-center align-items-center'>
                    <Col className='padding-to-fit' style={{ minWidth: '350px' }}>
                      {avgData.length > 0 && !isDashboardLoading ? (
                        <MetricsTable
                          data={avgData}
                          columns={esgLabel.map((el) => ({ key: el, label: el }))}
                        />
                      ) : (
                        <div className='d-flex justify-content-center align-items-center'>
                          <Spinner animation='border' role='status' className='my-3'>
                            <span style={{ visibility: 'hidden' }}>Loading...</span>
                          </Spinner>
                        </div>
                      )}
                    </Col>
                  </Col>
                  <div className='d-flex justify-content-end'>
                    <span style={{ fontSize: '.65rem' }}>
                      {translations.find((trans) => trans.code === 'ICS130')?.text?.toUpperCase()}
                    </span>
                  </div>
                </div>
              </details>
            </Col>
          </div>

          {/* ============ Card SROI ============ */}
          <div className='p-0 d-flex' style={{ minWidth: '400px', flex: '1 1 0' }}>
            <Col className='simple-box d-flex flex-column col-12'>
              <Row className='ml-0'>
                <h5 className='title-simple-box text-uppercase mt-1 pl-0'>SROI</h5>
              </Row>

              {/* Portafoglio SROI (ICS127): KPI list (Benefici / Costi / VANE / SROI Medio / Dev. Std.) */}
              <h6 className='subsection-title text-uppercase mt-3 pl-0' style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                {translations.filter((trans) => trans.code === 'ICS127').map((trans) => trans.text)}
              </h6>
              {Object.keys(stats).length > 0 && dataBenefits.length > 0 && !isDashboardLoading ? (
                <div className='sroi-details-padding mt-2'>
                  <MetricsTable
                    data={sroiDetailRows}
                    columns={sroiDetailColumns}
                    rowsPerColumn={5}
                    breakpoint={832}
                    showHeader={false}
                    valueRight={true}
                  />
                </div>
              ) : (
                <div className='d-flex justify-content-center align-items-center'>
                  <Spinner animation='border' role='status' className='my-3'>
                    <span style={{ visibility: 'hidden' }}>Loading...</span>
                  </Spinner>
                </div>
              )}

              {/* Accordion Dettaglio SROI (ICS129): chiuso di default, sincronizzato col Dettaglio ESG. */}
              <details className='accordion-detail mt-3' open={dettagliOpen}>
                <summary
                  className='subsection-title text-uppercase pl-0'
                  onClick={toggleDettagli}
                  style={{ fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', listStyle: 'none' }}>
                  <span style={{ display: 'inline-block', width: '1rem' }}>{dettagliOpen ? '▾' : '▸'}</span>
                  {translations.find((trans) => trans.code === 'ICS129')?.text}
                </summary>
                <div className='mt-2'>
                  <Row className='sroi-details-padding d-flex justify-content-center align-items-center mr-0'>
                    <Col>
                      {noOutData.length > 0 && !isDashboardLoading ? (
                        <MetricsTable data={noOutData.filter((el) => el.proponent !== 'portfolio')} />
                      ) : (
                        <div className='d-flex justify-content-center align-items-center'>
                          <Spinner animation='border' role='status' className='my-3'>
                            <span style={{ visibility: 'hidden' }}>Loading...</span>
                          </Spinner>
                        </div>
                      )}
                    </Col>
                  </Row>
                  <div className='d-flex justify-content-end'>
                    <span style={{ fontSize: '.65rem' }}>
                      {translations.find((trans) => trans.code === 'ICS130')?.text?.toUpperCase()}
                    </span>
                  </div>
                </div>
              </details>
            </Col>
          </div>
        </div>

        <div className='col-12 p-0 gap-2 d-md-flex flex-column flex-xxl-row dashboard-block time-series-charts'>
          {esgTimeSeriesRating?.length > 0 && <EsgTimingBarChart data={esgTimeSeriesRating} />}
          {sroiTimeSeriesRating?.length > 0 && <SroiTimingBarChart data={sroiTimeSeriesRating} />}
        </div>

        {/* Grafici G-01..G-13: solo superadmin. L'admin (role 3) vede sopra le
            serie storiche T-01/T-02 e la dashboard corrente. */}
        {isSuperAdmin && (
          <>
        {/* G-01 + G-02 affiancati (two-col del prototipo) */}
        <div className='row g-3 dashboard-block mx-0'>
          <div className='col-12 col-md-6 px-1 d-flex'>
            <DistributionAverageVerticalHistogram
              containerId='g01ProponentDistribution'
              title='Distribuzione dei progetti e degli investimenti per tipologia di proponente'
              data={distribProponentType.length > 0 ? distribProponentType : null}
            />
          </div>
          <div className='col-12 col-md-6 px-1 d-flex'>
            <DistributionAverageVerticalHistogram
              containerId='g02MacroAreaDistribution'
              title='Distribuzione dei progetti e degli investimenti per macro-aree'
              data={distribMacroArea.length > 0 ? distribMacroArea : null}
            />
          </div>
        </div>

        {/* G-03 full-width */}
        <div className='dashboard-block'>
          <DistributionAverageVerticalHistogram
            containerId='g03InvestmentClassDistribution'
            title='Numero di progetti e investimenti (€) per classi dimensionali dei progetti'
            data={distribInvestmentClass.length > 0 ? distribInvestmentClass : null}
          />
        </div>

        {/* G-04 + G-05 + G-06 affiancati (three-col del prototipo) */}
        <div className='row g-3 dashboard-block mx-0'>
          <div className='col-12 col-md-4 px-1 d-flex'>
            <RegionalHeatmapChart
              containerId='g04ProjectsByRegion'
              title='Distribuzione dei Progetti per Regione'
              visualizationId='28939357'
              data={projectsByRegion.length > 0 ? projectsByRegion : null}
            />
          </div>
          <div className='col-12 col-md-4 px-1 d-flex'>
            <RegionalHeatmapChart
              containerId='g05SroiByRegion'
              title='SROI medio per Regione'
              visualizationId='28939536'
              data={sroiAvgByRegion.length > 0 ? sroiAvgByRegion : null}
            />
          </div>
          <div className='col-12 col-md-4 px-1 d-flex'>
            <RegionalHeatmapChart
              containerId='g06EsgByRegion'
              title='Score ESG Medio Ponderato per Regioni'
              visualizationId='28939555'
              data={esgAvgByRegion.length > 0 ? esgAvgByRegion : null}
            />
          </div>
        </div>

        {/* G-07 + G-08 affiancati */}
        <div className='row g-3 dashboard-block mx-0'>
          <div className='col-12 col-md-6 px-1 d-flex'>
            <SingleBarVerticalHistogram
              containerId='g07SroiByMacroSector'
              title='SROI medio per macro-settore (Sport, Cultura, Audiovisivi)'
              data={sroiByMacroSector.length > 0 ? sroiByMacroSector : null}
            />
          </div>
          <div className='col-12 col-md-6 px-1 d-flex'>
            <SingleBarVerticalHistogram
              containerId='g08SroiByIntervention'
              title='SROI medio per tipologia di intervento'
              data={sroiAvgByIntervention.length > 0 ? sroiAvgByIntervention : null}
            />
          </div>
        </div>

        {/* G-09 full-width.
            TODO: tornare a HorizontalBarChart (template 28849316) quando sarà
            pubblicato su Flourish; oggi 28849316 risponde 403 via Live API. */}
        <div className='dashboard-block'>
          <SingleBarVerticalHistogram
            containerId='g09EsgByIntervention'
            title='Score ESG Medio Ponderato per tipologia di intervento'
            data={esgAvgByIntervention.length > 0 ? esgAvgByIntervention : null}
          />
        </div>

        {/* G-10 + G-11 affiancati (tabelle native) */}
        <div className='row g-3 dashboard-block mx-0'>
          <div className='simple-box col-12 col-md-6'>
            <h5 className='title-simple-box text-uppercase'>SROI medio per classi di investimento</h5>
            <table className='table table-sm mt-2'>
              <thead>
                <tr>
                  <th>Classi d'Investimento</th>
                  <th className='text-right'>N. Progetti</th>
                  <th className='text-right'>% sul Totale</th>
                  <th className='text-right'>SROI Medio</th>
                </tr>
              </thead>
              <tbody>
                {tableSroiByInvestmentClass.map((row, i) => (
                  <tr key={i}>
                    <td>{row.class}</td>
                    <td className='text-right'>{row.count}</td>
                    <td className='text-right'>{row.pct}%</td>
                    <td className='text-right'>
                      {row.sroiAvg !== null && row.sroiAvg !== undefined
                        ? Number(row.sroiAvg).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className='simple-box col-12 col-md-6'>
            <h5 className='title-simple-box text-uppercase'>Score ESG medio ponderato per macro-settore</h5>
            <table className='table table-sm mt-2'>
              <thead>
                <tr>
                  <th>Settore</th>
                  <th className='text-right'>Score ESG MP</th>
                  <th className='text-center'>Rating ESG MP</th>
                </tr>
              </thead>
              <tbody>
                {tableEsgByMacroSector.map((row, i) => (
                  <tr key={i}>
                    <td>{row.sector}</td>
                    <td className='text-right'>
                      {row.esgMp !== null && row.esgMp !== undefined
                        ? Number(row.esgMp).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '-'}
                    </td>
                    <td className='text-center'>{row.rating || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* G-12 + G-13 affiancati (tabelle native) */}
        <div className='row g-3 dashboard-block mx-0'>
          <div className='simple-box col-12 col-md-6'>
            <h5 className='title-simple-box text-uppercase'>Score ESG medio ponderato per tipologia di proponente</h5>
            <table className='table table-sm mt-2'>
              <thead>
                <tr>
                  <th>Tipo Soggetto</th>
                  <th className='text-right'>Score ESG MP</th>
                  <th className='text-center'>Rating ESG MP</th>
                </tr>
              </thead>
              <tbody>
                {tableEsgByProponentGroup.map((row, i) => (
                  <tr key={i}>
                    <td>{row.proponentGroup}</td>
                    <td className='text-right'>
                      {row.esgMp !== null && row.esgMp !== undefined
                        ? Number(row.esgMp).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '-'}
                    </td>
                    <td className='text-center'>{row.rating || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className='simple-box col-12 col-md-6'>
            <h5 className='title-simple-box text-uppercase'>SROI medio per Macro Area (Nord, Centro, Sud e Isole)</h5>
            <table className='table table-sm mt-2'>
              <thead>
                <tr>
                  <th>Macroarea</th>
                  <th className='text-right'>SROI Medio</th>
                </tr>
              </thead>
              <tbody>
                {tableSroiByMacroArea.map((row, i) => (
                  <tr key={i}>
                    <td>{row.macroArea}</td>
                    <td className='text-right'>
                      {row.sroiAvg !== null && row.sroiAvg !== undefined
                        ? Number(row.sroiAvg).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
          </>
        )}
      </div>

      <div className='simple-box scenaritab'>
        <div className='row d-flex align-items-center mb-3'>
          <div className='col-6'>
            <h5 className='title-simple-box'>Esplora progetti</h5>
          </div>
          <div className='col-6 text-right'>
            <ButtonToolbar className='justify-content-end align-items-stretch'>
              <Button variant='info' href='/valutazione' className='lineheight-initial'>
                <Row className='align-items-center py-1 px-3'>
                  <IcoPlus className='light-color ico-small plus mr-1' />
                  <span className='d-md-inline'>Nuovo Progetto</span>
                </Row>
              </Button>
              <OverlayTrigger placement='top' overlay={downloadTooltip}>
                <Button
                  variant='info'
                  onClick={!isLoading ? handleDownload : null}
                  aria-label='Download Excel'
                  className='ml-2 lineheight-initial'
                  disabled={isLoading}>
                  <Row
                    className='align-items-center justify-content-center'
                    style={{ padding: '0.25rem 0.7rem' }}>
                    {isLoading ? (
                      'Loading…'
                    ) : (
                      <DownloadFile width='23px' className='light-color m-0' />
                    )}
                  </Row>
                </Button>
              </OverlayTrigger>
              <InputGroup className='search-table align-items-center lineheight-initial'>
                <FormControl
                  type='text'
                  id='filter-text-box'
                  placeholder='Search...'
                  className='d-none d-md-block'
                  onInput={(e) => onFilterTextBoxChanged(e)}
                />
                <Lens />
              </InputGroup>
            </ButtonToolbar>
          </div>
        </div>

        <div className='row'>
          <div className='col-12' id='grid-wrapper'>
            {Object.keys(stats).length > 0 ? (
              <>
                <div className='ag-theme-alpine projects-list-table' style={{ height: '70vh', minHeight: '500px' }}>
                  <AgGridReact
                    ref={gridRef}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    tooltipShowDelay={0}
                    tooltipHideDelay={2000}
                    tooltipMouseTrack={true}
                    onFirstDataRendered={onFirstDataRendered}
                    onGridReady={onGridReady}
                    onModelUpdated={onModelUpdated}
                    rowModelType='infinite'
                    datasource={infiniteDataSource}
                    cacheBlockSize={20}
                    maxBlocksInCache={10}
                    pagination={true}
                    paginationPageSize={20}
                    headerHeight={50}
                    rowHeight={50}
                    localeText={localeText}
                  />
                </div>
                {/* Selettore "Progetti per pagina" fuori dalla griglia ad altezza
                    fissa: prima usciva dal bordo della card. */}
                <div className='example-header'>
                  Progetti per pagina:&nbsp;
                  <select onChange={onPageSizeChanged} id='page-size'>
                    {optionsPagination}
                  </select>
                </div>
              </>
            ) : (
              <div className='text-center my-4'>
                <Spinner animation='border' role='status'>
                  <span style={{ visibility: 'hidden' }}>Loading...</span>
                </Spinner>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectsTab;
